import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import { LineSeries, Point, SliceData } from '@nivo/line'
import { useTranslation } from 'react-i18next'
import { intersectionWith } from 'lodash'
import { addHours, differenceInHours } from 'date-fns'
import { Section } from '../../common/Section'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { FormGroup } from '../../form/FormGroup'
import { LineChart } from '../../common/charts/LineChart'
import { hoursInYear } from '../../../util/hours-in-year'
import { ChartHourSeriesSelect } from './ChartHourSeriesSelect'
import {
  ChartTimeResolutionSelect,
  TimeResolution,
} from './ChartTimeResolutionSelect'
import { ChartTimePicker, getStartDateByTimeSpan } from './ChartTimePicker'
import { ChartTimeSpanSelect, TimeSpan } from './ChartTimeSpanSelect'
import {
  createGetValuesByResolution,
  getEndDateTime,
  mapValuesToDataPoints,
} from './chart-util'
import { ChartType, ChartTypeToggle } from './ChartTypeToggle'
import { formatEnergy, formatPower } from '../../../util/formatters'
import { ChartProps, EnergyWithName } from './use-chart-util'
import { useTimeChartFormatters } from './use-time-chart-formatters'

type Props = ChartProps & {
  startOfYearDate: Date
}
export function TimeChart({
  titlePrefix,
  chartUtil,
  startOfYearDate,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { flowOptions, flows, defaultFlows } = chartUtil

  const [selectedFlows, setSelectedFlows] = useState<EnergyWithName[]>([])
  const [flowNames, setFlowNames] = useState<(EnergyType | string)[]>(
    defaultFlows.map((flow) => flow.name),
  )
  const [chartType, setChartType] = useState(ChartType.Power)
  const [resolution, setResolution] = useState(TimeResolution.Hour)
  const [timeSpan, setTimeSpan] = useState(TimeSpan.Year)
  const [selectedDate, setSelectedDate] = useState(
    getStartDateByTimeSpan(startOfYearDate, timeSpan),
  )

  const { getXAxisValueFormatter, getTimeFormatter } = useTimeChartFormatters(
    timeSpan,
    selectedDate,
  )

  const handleTimeSpanChange = (value: TimeSpan, date?: Date) => {
    setTimeSpan(value)
    setSelectedDate(getStartDateByTimeSpan(date ?? selectedDate, value))
  }

  const handleChartTypeChange = (value: ChartType) => {
    setChartType(value)

    if (value === ChartType.Energy) {
      setResolution(TimeResolution.Month)
      handleTimeSpanChange(TimeSpan.Year)
    } else {
      setResolution(TimeResolution.Hour)
    }
  }

  const handleFlowNamesChange = useCallback(
    (values: string[]) => {
      setFlowNames(values)

      const newFlows = intersectionWith(
        flows,
        values,
        (flow, name) => flow.name === name,
      )
      setSelectedFlows(newFlows)
    },
    [flows],
  )

  useEffect(() => {
    handleFlowNamesChange(defaultFlows.map((flow) => flow.name))
  }, [flowOptions, defaultFlows, handleFlowNamesChange])

  useEffect(() => {
    const newFlows = intersectionWith(
      flows,
      flowNames,
      (flow, name) => flow.name === name,
    )
    setSelectedFlows(newFlows)
  }, [flows, flowNames])

  const getValuesByResolution = createGetValuesByResolution(startOfYearDate)

  const mapFlowToGraphData = (flow: EnergyWithName) => {
    const hourSeries =
      flow.hourSeries.length === 0
        ? (new Array(hoursInYear).fill(0) as number[])
        : flow.hourSeries
    const startHourIndex = differenceInHours(selectedDate, startOfYearDate)
    const endHourIndex = differenceInHours(
      getEndDateTime(timeSpan, selectedDate),
      startOfYearDate,
    )

    const values = hourSeries.slice(startHourIndex, endHourIndex)

    const summedValues = getValuesByResolution(values, resolution)

    return {
      id: flow.name,
      data: summedValues.map(mapValuesToDataPoints),
    }
  }

  const data = selectedFlows.map(mapFlowToGraphData)

  const valueFormatter =
    chartType === ChartType.Energy ? formatEnergy : formatPower

  const onClickHandler = (
    datum: Readonly<Point<LineSeries>> | Readonly<SliceData<LineSeries>>,
  ): void => {
    if (chartType === ChartType.Energy) {
      return
    }

    if ('data' in datum && typeof datum.data.x === 'number') {
      const hour = Number(datum.data.x)

      if (timeSpan === TimeSpan.Year) {
        const newDate = addHours(selectedDate, hour)
        handleTimeSpanChange(TimeSpan.Month, newDate)
      } else if (timeSpan === TimeSpan.Month) {
        const newDate = addHours(selectedDate, hour)
        handleTimeSpanChange(TimeSpan.Day, newDate)
      } else {
        handleTimeSpanChange(TimeSpan.Year, startOfYearDate)
      }
    }
  }

  return (
    <Section title={`${titlePrefix}: ${t('charts.consumption.time.title')}`}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" flexDirection="row">
          <FormGroup mr={2}>
            <ChartHourSeriesSelect
              values={flowNames}
              onChange={handleFlowNamesChange}
              options={flowOptions}
            />
          </FormGroup>

          <FormGroup>
            <ChartTypeToggle
              value={chartType}
              onChange={handleChartTypeChange}
            />
          </FormGroup>
        </Box>

        <Box display="flex" flexDirection="row">
          <FormGroup>
            <ChartTimeResolutionSelect
              value={resolution}
              onChange={setResolution}
            />
          </FormGroup>

          <FormGroup ml={2}>
            <ChartTimeSpanSelect
              value={timeSpan}
              onChange={handleTimeSpanChange}
              chartType={chartType}
            />
          </FormGroup>

          <FormGroup ml={2}>
            <ChartTimePicker
              timeSpan={timeSpan}
              selectedDate={selectedDate}
              onChange={setSelectedDate}
              startOfYearDate={startOfYearDate}
            />
          </FormGroup>
        </Box>
      </Box>

      <Box width="100%" height={540} borderRadius={6}>
        {data.length > 0 && (
          <LineChart
            data={data}
            xFormatter={getTimeFormatter(resolution)}
            yFormatter={valueFormatter}
            xAxisFormatter={getXAxisValueFormatter(resolution)}
            onClick={onClickHandler}
          />
        )}
      </Box>
    </Section>
  )
}
